import { useState, useEffect, useMemo } from "react";
import { Link as RouterLink, Outlet, useParams } from "react-router-dom";
import {
    alpha,
    AppBar,
    Box,
    Button,
    Collapse,
    Container,
    Divider,
    Fab,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Toolbar,
    Typography,
    Zoom,
    useMediaQuery,
    useScrollTrigger,
    useTheme,
} from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import {
    TOP_NAV,
    TOC,
    getSectionById,
    docPath,
} from "../../config/documentationContent";
import { TocNavList } from "./DocComponents";
import ThemeToggle from "../../components/ThemeToggle";

export default function DocumentationLayout() {
    const theme = useTheme();
    const { sectionId } = useParams();
    const activeSection = sectionId || TOC[0].id;
    const currentSection = getSectionById(activeSection);

    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [tocOpen, setTocOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [tocQuery, setTocQuery] = useState("");

    const showBackToTop = useScrollTrigger({ disableHysteresis: true, threshold: 400 });

    const filteredToc = useMemo(() => {
        const q = tocQuery.trim().toLowerCase();
        if (!q) return TOC;
        return TOC.filter((item) => item.label.toLowerCase().includes(q));
    }, [tocQuery]);

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", fn, { passive: true });
        return () => window.removeEventListener("scroll", fn);
    }, []);

    const closeMenus = () => {
        setMobileNavOpen(false);
        setTocOpen(false);
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const navLinkSx = {
        color: "text.secondary",
        fontFamily: '"DM Sans", sans-serif',
        fontSize: 13,
        fontWeight: 500,
        px: 1.5,
        "&:hover": { color: "text.primary", bgcolor: "transparent" },
    };

    const tocSearchField = (
        <TextField
            size="small"
            fullWidth
            placeholder="Search sections…"
            value={tocQuery}
            onChange={(e) => setTocQuery(e.target.value)}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    </InputAdornment>
                ),
            }}
            sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: 13,
                    bgcolor: alpha("#ffffff", 0.03),
                    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                    "&:hover fieldset": { borderColor: alpha("#e8c97e", 0.35) },
                    "&.Mui-focused fieldset": { borderColor: "primary.main" },
                },
            }}
        />
    );

    return (
        <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    bgcolor: scrolled ? alpha("#0d1014", 0.92) : alpha("#0a0c0f", 0.88),
                    backdropFilter: scrolled ? "blur(20px)" : "blur(12px)",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
            >
                <Toolbar sx={{ maxWidth: 1400, width: "100%", mx: "auto", px: { xs: 2, md: 3 }, minHeight: { xs: 56, md: 64 } }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                borderRadius: "4px",
                                bgcolor: "primary.main",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <MenuBookOutlinedIcon sx={{ fontSize: 18, color: "#0a0c0f" }} />
                        </Box>
                        <Typography
                            component={RouterLink}
                            to={docPath(TOC[0].id)}
                            sx={{
                                fontFamily: '"DM Sans", sans-serif',
                                fontWeight: 700,
                                fontSize: "1.05rem",
                                color: "text.primary",
                                textDecoration: "none",
                            }}
                        >
                            SMS Docs
                        </Typography>
                    </Stack>
                    {!isMobile ? (
                        <Stack direction="row" spacing={0} alignItems="center">
                            {TOP_NAV.map((item) => (
                                <Button
                                    key={item.id}
                                    component={RouterLink}
                                    to={docPath(item.id)}
                                    sx={navLinkSx}
                                >
                                    {item.label}
                                </Button>
                            ))}
                            <ThemeToggle size="small" sx={{ ml: 1 }} />
                            <Button
                                component={RouterLink}
                                to="/login"
                                variant="outlined"
                                size="small"
                                sx={{
                                    ml: 2,
                                    borderColor: "rgba(255,255,255,0.15)",
                                    color: "text.primary",
                                    fontFamily: '"DM Sans", sans-serif',
                                    "&:hover": { borderColor: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.04)" },
                                }}
                            >
                                Sign in
                            </Button>
                        </Stack>
                    ) : (
                        <IconButton onClick={() => setMobileNavOpen((v) => !v)} sx={{ color: "text.primary" }} aria-label="menu">
                            {mobileNavOpen ? <CloseIcon /> : <MenuIcon />}
                        </IconButton>
                    )}
                </Toolbar>
                {isMobile && mobileNavOpen && (
                    <Box sx={{ px: 2, pb: 2, borderTop: "1px solid rgba(255,255,255,0.06)", bgcolor: alpha("#0d1014", 0.98) }}>
                        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                            {TOP_NAV.map((item) => (
                                <Button
                                    key={item.id}
                                    component={RouterLink}
                                    to={docPath(item.id)}
                                    fullWidth
                                    onClick={closeMenus}
                                    sx={{ justifyContent: "flex-start", color: "text.secondary", fontFamily: '"DM Sans", sans-serif' }}
                                >
                                    {item.label}
                                </Button>
                            ))}
                            <Button component={RouterLink} to="/login" variant="outlined" fullWidth sx={{ mt: 1, borderColor: "rgba(255,255,255,0.12)", color: "text.primary" }}>
                                Sign in
                            </Button>
                        </Stack>
                    </Box>
                )}
            </AppBar>

            <Container maxWidth="xl" sx={{ pt: { xs: 10, md: 11 }, pb: 8, px: { xs: 2, md: 3 } }}>
                <Box sx={{ mb: { xs: 3, md: 4 }, pt: { xs: 2, md: 3 } }}>
                    <Typography variant="h2" sx={{ fontSize: { xs: "2rem", md: "2.75rem" }, color: "text.primary", mb: 1 }}>
                        {currentSection?.title || "Documentation"}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 640 }}>
                        {currentSection?.description ||
                            "Help centre for the School Management System — setup, daily operations, finance, and portals."}
                    </Typography>
                </Box>

                {isMobile && (
                    <Paper elevation={0} sx={{ mb: 3, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                        <Button
                            fullWidth
                            onClick={() => setTocOpen((v) => !v)}
                            endIcon={tocOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{
                                justifyContent: "space-between",
                                py: 1.5,
                                px: 2,
                                color: "text.primary",
                                fontFamily: '"DM Sans", sans-serif',
                                fontWeight: 600,
                            }}
                        >
                            Table of contents
                        </Button>
                        <Collapse in={tocOpen}>
                            <Divider />
                            <Box sx={{ py: 1.5, px: 1.5 }}>
                                {tocSearchField}
                                <TocNavList items={filteredToc} activeId={activeSection} onItemClick={closeMenus} compact />
                            </Box>
                        </Collapse>
                    </Paper>
                )}

                <Grid container spacing={4}>
                    {!isMobile && (
                        <Grid item md={3} lg={2.5}>
                            <Paper
                                elevation={0}
                                sx={{
                                    position: "sticky",
                                    top: 88,
                                    maxHeight: "calc(100vh - 104px)",
                                    overflowY: "auto",
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: "primary.main",
                                        letterSpacing: "0.1em",
                                        fontFamily: '"DM Sans", sans-serif',
                                        display: "block",
                                        mb: 1.5,
                                    }}
                                >
                                    Modules
                                </Typography>
                                {tocSearchField}
                                <TocNavList items={filteredToc} activeId={activeSection} />
                            </Paper>
                        </Grid>
                    )}

                    <Grid item xs={12} md={9} lg={9.5}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, md: 4 },
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "background.paper",
                            }}
                        >
                            <Outlet />
                        </Paper>

                        <Box
                            component="footer"
                            sx={{
                                mt: 4,
                                py: 3,
                                textAlign: "center",
                                borderTop: `1px solid ${alpha("#e8c97e", 0.2)}`,
                            }}
                        >
                            <Typography variant="body2" sx={{ color: "text.secondary", fontFamily: '"DM Sans", sans-serif' }}>
                                School Management System Documentation · For support contact your system administrator
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            <Zoom in={showBackToTop}>
                <Fab
                    color="primary"
                    size="medium"
                    onClick={scrollToTop}
                    aria-label="Back to top"
                    sx={{
                        position: "fixed",
                        bottom: 24,
                        right: 24,
                        zIndex: theme.zIndex.speedDial,
                        bgcolor: "primary.main",
                        color: "#0a0c0f",
                        boxShadow: `0 4px 20px ${alpha("#e8c97e", 0.35)}`,
                        "&:hover": { bgcolor: "primary.light" },
                    }}
                >
                    <KeyboardArrowUpIcon />
                </Fab>
            </Zoom>
        </Box>
    );
}
